import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
    ChangeVBorVBPPlanModalContent,
} from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/pages/Strategy/componets/ChangeVBorVBPPlanModalContent';
import { BUTTON_TYPE_SECONDARY } from 'ui-library/components/Button/constants';
import { ACTIVE } from 'constants/portfolioStatuses';
import {
    EXTERNAL_PRODUCTS, STRATEGIES,
} from 'constants/constants';
import ServiceManager from '../../../services/ServiceManager';
import { Modal } from '../../../ui-library';
import Error from '../../../components/AtomicStructure/atoms/Error';
import { Change3aor3apPlanModalContent } from '../pages/Portfolios/pages/SinglePortfolioView/pages/Strategy/componets/Change3aor3apPlanModalContent';
import arrayContains from '../../../utils/arrayUtils';

// --- Types & Interfaces ---

type TFunction = (key: string) => string;

interface ModifyPlanArgs {
    path: string;
    action: string;
    id: string | number;
    t: TFunction;
    status: string;
    navigate: NavigateFunction;
    productExternalId?: string;
    amount?: number;
}

interface StrategyErrorArgs {
    is3bProduct: boolean;
    isZic: boolean;
    isAzp: boolean;
    selectedStrategyId: string | number;
    setStrategySelectedError: (error: { message: string; isError: boolean } | false) => void;
}

interface ModalArgs {
    t: TFunction;
    path: string;
    navigate: NavigateFunction;
    isVBP?: boolean;
    isVB?: boolean;
    is3ap?: boolean;
    is3a?: boolean;
}

const planStatus = {
    accepted: 'accepted',
};

// --- Helper Functions ---

const showModal = (t: TFunction, action: string, error?: string): void => {
    const content = (
        <div>
            <span>{t(`clientDashboard.unableTo${action}Body`)}</span>
            {error && <Error>{error}</Error>}
        </div>
    );

    Modal.info({
        title: t(`clientDashboard.unableTo${action}Title`),
        content,
    });
};

const showVBorVBPModal = ({
    t, isVBP, isVB, path, navigate,
}: ModalArgs): void => {
    let modal: { destroy: () => void };

    const closeModal = () => {
        if (modal?.destroy) {
            modal.destroy();
        }
    };

    modal = Modal.info({
        title: t('clientDashboard.changeVBPlan.title'),
        content: (
            <ChangeVBorVBPPlanModalContent
                isVBP={!!isVBP}
                isVB={!!isVB}
                path={path}
                navigate={navigate}
                onNavigate={closeModal}
            />
        ),
        okText: t('clientDashboard.cancel'),
        okType: BUTTON_TYPE_SECONDARY,
        width: 680,
    });
};

const show3aor3apModal = ({
    t, is3ap, is3a, path, navigate,
}: ModalArgs): void => {
    let modal: { destroy: () => void };

    const closeModal = () => {
        if (modal?.destroy) {
            modal.destroy();
        }
    };

    modal = Modal.info({
        title: t('clientDashboard.change3aPlan.title'),
        content: (
            <Change3aor3apPlanModalContent
                is3ap={!!is3ap}
                is3a={!!is3a}
                path={path}
                navigate={navigate}
                onNavigate={closeModal}
            />
        ),
        okText: t('clientDashboard.cancel'),
        okType: BUTTON_TYPE_SECONDARY,
        width: 680,
    });
};

// --- Exported Functions ---

export const modifyPlan = async ({
    path, action, id, t, status, navigate, productExternalId, amount = 0,
}: ModifyPlanArgs): Promise<void> => {
    const executeActivePlan = () => {
        if (productExternalId && (arrayContains([EXTERNAL_PRODUCTS.aaa, EXTERNAL_PRODUCTS.aaap], productExternalId))) {
            show3aor3apModal({
                t,
                is3ap: arrayContains([EXTERNAL_PRODUCTS.aaap], productExternalId) ? (amount >= 30000) : amount >= 50000,
                is3a: true,
                path,
                navigate,
            });
        } else if (productExternalId && (arrayContains([EXTERNAL_PRODUCTS.fz, EXTERNAL_PRODUCTS.fzp], productExternalId))) {
            showVBorVBPModal({
                t,
                isVBP: arrayContains([EXTERNAL_PRODUCTS.fzp], productExternalId) ? (amount >= 120000) : amount >= 200000,
                isVB: true,
                path,
                navigate,
            });
        } else {
            navigate(path);
        }
    };

    try {
        if (status === ACTIVE) {
            executeActivePlan();
        } else {
            const { data } = await ServiceManager.planService('getPlanStatus', [{ planId: id }]);

            if (data === planStatus.accepted) {
                executeActivePlan();
            } else {
                showModal(t, action);
            }
        }
    } catch (err: any) {
        showModal(t, action, err.response?.data);
    }
};

export const findStrategyErrorMessage = ({
    is3bProduct, isZic, isAzp, selectedStrategyId, setStrategySelectedError,
}: StrategyErrorArgs): void => {
    if (is3bProduct && isZic) {
        if (selectedStrategyId === STRATEGIES.income || selectedStrategyId === STRATEGIES.capital) {
            setStrategySelectedError({
                message:
                selectedStrategyId === STRATEGIES.capital ? 'portfolio.createNewPortfolio.selectStrategy.zicStrategyCapitalWarn'
                    : 'portfolio.createNewPortfolio.selectStrategy.zicStrategyWarn',
                isError: false,
            });
        } else if (selectedStrategyId === STRATEGIES.interest) {
            setStrategySelectedError({ message: 'portfolio.createNewPortfolio.selectStrategy.zicStrategyError', isError: true });
        } else {
            setStrategySelectedError(false);
        }
    } else if (is3bProduct && isAzp && selectedStrategyId === STRATEGIES.interest) {
        setStrategySelectedError({ message: 'portfolio.createNewPortfolio.selectStrategy.strategySelectedError', isError: true });
    } else {
        setStrategySelectedError(false);
    }
};

export const getMinimumWithdrawalPhaseDurationBasedOnStrategy = (strategyId: string | number): number => {
    switch (strategyId) {
        case STRATEGIES.income:
            return 6;
        case STRATEGIES.balanced:
            return 11;
        case STRATEGIES.growth:
            return 15;
        case STRATEGIES.capital:
            return 15;
        default:
            return 0;
    }
};
